'use client'

import * as React from 'react'
import { LazyMotion, domAnimation, m } from 'framer-motion'
import { Hash, FileSignature, Tag, LayoutGrid, BarChart3, Check } from 'lucide-react'
import { EASE, SPRING } from '@/lib/motion'
import { ExplainerFrame } from './explainer-frame'

// The whole product in one line: a single Instagram post travels through
// the five stages and comes out the other end as an attributed order.
// Each stage lights when the post reaches it and reports what it did.

const STAGES = [
  { key: 'collect', icon: Hash,          label: 'Collect', result: 'Found via #SummerWithLumen' },
  { key: 'rights',  icon: FileSignature, label: 'Rights',  result: 'Creator signed, 2-year licence' },
  { key: 'tag',     icon: Tag,           label: 'Tag',     result: 'Matched: Silver-Toned Watch' },
  { key: 'display', icon: LayoutGrid,    label: 'Display', result: 'Live on PDP + homepage reel' },
  { key: 'measure', icon: BarChart3,     label: 'Measure', result: '3 orders · $74.28 attributed' },
] as const

const STEP = 0.55

export function PipelineExplainer({ className }: { className?: string }) {
  return (
    <ExplainerFrame
      eyebrow="How Idukki works"
      title="One post, collected to attributed"
      caption="A customer post enters at the left and leaves as a measured order. Every stage is automatic unless you choose to review it."
      className={className}
    >
      {({ play, reduce, d }) => (
        <LazyMotion features={domAnimation}>
          <div className="relative">
            {/* The rail the post travels along */}
            <div className="absolute left-[10%] right-[10%] top-6 h-px bg-border" aria-hidden />
            <m.div
              className="absolute left-[10%] top-6 h-px origin-left bg-primary"
              style={{ width: '80%' }}
              initial={reduce ? false : { scaleX: 0 }}
              animate={play ? { scaleX: 1 } : undefined}
              transition={{ duration: STEP * 4, ease: EASE, delay: d(0.2) }}
              aria-hidden
            />
            <ol className="relative grid grid-cols-5 gap-2">
              {STAGES.map((s, i) => {
                const Icon = s.icon
                const at = d(0.2 + i * STEP)
                return (
                  <li key={s.key} className="flex flex-col items-center text-center">
                    <m.span
                      className="relative z-10 flex h-12 w-12 items-center justify-center rounded-full border-2 bg-card text-muted-foreground"
                      initial={reduce ? false : { scale: 0.92 }}
                      animate={play ? { scale: 1 } : undefined}
                      transition={{ ...SPRING, delay: at }}
                    >
                      <Icon className="h-5 w-5" aria-hidden />
                      {/* Lit state: a primary ring + icon that fade in over the muted base (opacity only). */}
                      <m.span
                        className="absolute inset-[-2px] flex items-center justify-center rounded-full border-2 border-primary bg-card text-primary"
                        initial={reduce ? { opacity: 1 } : { opacity: 0 }}
                        animate={play ? { opacity: 1 } : undefined}
                        transition={{ duration: 0.3, ease: EASE, delay: at }}
                        aria-hidden
                      >
                        <Icon className="h-5 w-5" />
                      </m.span>
                      <m.span
                        className="absolute -right-1 -top-1 z-10 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-600 text-white"
                        initial={reduce ? false : { opacity: 0, scale: 0.4 }}
                        animate={play ? { opacity: 1, scale: 1 } : undefined}
                        transition={{ ...SPRING, delay: at + 0.25 }}
                        aria-hidden
                      >
                        <Check className="h-2.5 w-2.5" />
                      </m.span>
                    </m.span>
                    <p className="mt-2 text-xs font-semibold text-foreground">{s.label}</p>
                    <m.p
                      className="mt-1 hidden text-[11px] leading-snug text-muted-foreground sm:block"
                      initial={reduce ? false : { opacity: 0, y: 6 }}
                      animate={play ? { opacity: 1, y: 0 } : undefined}
                      transition={{ duration: 0.35, ease: EASE, delay: at + 0.2 }}
                    >
                      {s.result}
                    </m.p>
                  </li>
                )
              })}
            </ol>

            {/* The travelling post: one card, five resting positions.
                Rendered as five copies so only transform/opacity animate. */}
            <div className="relative mt-5 grid grid-cols-5 gap-2" aria-hidden>
              {STAGES.map((s, i) => {
                const at = d(0.2 + i * STEP)
                const last = i === STAGES.length - 1
                return (
                  <div key={s.key} className="flex justify-center">
                    <m.div
                      className="flex w-[92%] max-w-[120px] items-center gap-2 rounded-lg border bg-secondary/40 p-1.5"
                      initial={reduce ? { opacity: last ? 1 : 0 } : { opacity: 0, x: -10 }}
                      animate={
                        play
                          ? last
                            ? { opacity: 1, x: 0 }
                            : { opacity: [0, 1, 1, 0], x: [-10, 0, 0, 10] }
                          : undefined
                      }
                      transition={
                        last
                          ? { duration: 0.35, ease: EASE, delay: at }
                          : { duration: STEP, ease: EASE, delay: at, times: [0, 0.25, 0.75, 1] }
                      }
                    >
                      <span className="h-7 w-7 shrink-0 rounded-md bg-gradient-to-br from-primary/70 to-primary/30" />
                      <span className="truncate text-[10px] font-medium text-foreground">@darcy.s</span>
                    </m.div>
                  </div>
                )
              })}
            </div>

            <m.p
              className="mt-4 text-xs text-muted-foreground sm:hidden"
              initial={reduce ? false : { opacity: 0 }}
              animate={play ? { opacity: 1 } : undefined}
              transition={{ duration: 0.3, delay: d(0.2 + STAGES.length * STEP) }}
            >
              Result: 3 orders, $74.28 attributed to one post.
            </m.p>
          </div>
        </LazyMotion>
      )}
    </ExplainerFrame>
  )
}
